import { Announcement03, DotsVertical, SlashCircle01, UserCheck01, Users01 } from "@untitledui/icons";
import { useCallback, useMemo, useState } from "react";
import { useBlockCreator } from "@/api/blocklist";
import { GroupedActionMenu } from "@/components/action-menu/grouped-action-menu";
import { UnboxTable } from "@/components/application/table/unbox-table";
import type { UnboxTableColumn, UnboxTableProps } from "@/components/application/table/unbox-table.types";
import { InviteToCollaborationModal } from "@/components/assurance/invite-to-collaboration-modal";
import { Avatar } from "@/components/base/avatar/avatar";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { CreatorBlockedBadge } from "@/components/blocklist/creator-blocked-badge";
import { UnblockCreatorModal } from "@/components/blocklist/unblock-creator-modal";
import { CreatorCohortCellContent } from "@/components/creator-cohorts/creator-cohort-cell";
import { NETWORK_COLUMN_TOOLTIP, NetworkCellContent } from "@/components/creator-network/creator-network";
import { CreatorTagsCellContent } from "@/components/creator-tags/creator-tags-cell";
import { MessageCreatorsDialog } from "@/components/message-creator-dialogs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCurrency } from "@/hooks/use-currency";
import type { useExplorerDateFilter } from "@/hooks/use-explorer-date-filter";
import { buildCreatorActionGroups } from "@/lib/creator-action-groups";
import { BulkCohortModal } from "@/pages/creator-cohorts/bulk-cohort-modal";
import { AddtoListsModal } from "@/pages/creator-lists/add-to-lists-modal";
import {
  GenderDemoCellContent,
  MainAgeDemoCellContent,
  MatchQualityCellContent,
  TrendNumberCellContent,
} from "@/pages/explorers/columns/shared-cells";
import { TrendBadge } from "@/pages/explorers/utils";
import { error as toastError, success as toastSuccess } from "@/providers/ToastProvider";
import { getCreatorName } from "@/utils/creator-name";
import { useCreatorMetricColumns } from "./columns/creator-metric-columns";
import { useCreatorColumnCatalog } from "./creator-columns";
import type { TransformedCreator } from "./creator-explorer";

const videoFormatter = new Intl.NumberFormat("en-US", { compactDisplay: "short" });

export interface CreatorExplorerTableProps
  extends Omit<
    UnboxTableProps<TransformedCreator>,
    "columns" | "rowId" | "emptyState" | "filteredEmptyState" | "size" | "rounded" | "className" | "aria-label"
  > {
  dateRange: ReturnType<typeof useExplorerDateFilter>["dateRange"];
  creatorTagsEnabled: boolean;
  onClearFilters: () => void;
  onManageAllTags: () => void;
  onManageAllCohorts: () => void;
}

export function CreatorExplorerTable({
  rows,
  onClickRow,
  selection,
  sorting,
  onHiddenSortColumn,
  pagination,
  hasActiveFilters,
  dateRange,
  creatorTagsEnabled,
  onClearFilters,
  onManageAllTags,
  onManageAllCohorts,
}: CreatorExplorerTableProps) {
  const [rowInviteCreatorId, setRowInviteCreatorId] = useState<string>();
  const [rowMessageCreatorId, setRowMessageCreatorId] = useState<string>();
  const [rowCohortCreatorId, setRowCohortCreatorId] = useState<string>();
  const [addToListsCreatorId, setAddToListsCreatorId] = useState<string>();
  const [unblockTarget, setUnblockTarget] = useState<{ creatorOpenId: string; creatorName: string }>();
  const blockCreator = useBlockCreator();

  const handleBlockCreator = useCallback(
    async (_creatorId: string, tiktokOpenId: string) => {
      try {
        await blockCreator.mutateAsync(tiktokOpenId);
        toastSuccess("1 Creator blocked. Unblock anytime from Operations/Blocked Creators.");
      } catch {
        toastError("Failed to block creator");
      }
    },
    [blockCreator],
  );

  const rowActions = useMemo(
    () => ({
      onInvite: setRowInviteCreatorId,
      onMessage: setRowMessageCreatorId,
      onAddToList: setAddToListsCreatorId,
      onAddToCohort: setRowCohortCreatorId,
      onBlock: handleBlockCreator,
      onUnblock: (creatorOpenId: string, creatorName: string) => setUnblockTarget({ creatorOpenId, creatorName }),
    }),
    [handleBlockCreator],
  );
  const periodLabel = useMemo(() => {
    if (!dateRange) return null;
    const start = new Date(dateRange.start.year, dateRange.start.month - 1, dateRange.start.day);
    const end = new Date(dateRange.end.year, dateRange.end.month - 1, dateRange.end.day);
    const days = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return `Last ${days} Days`;
  }, [dateRange]);
  const currency = useCurrency();
  const metricColumns = useCreatorMetricColumns<TransformedCreator>();
  const columnCatalog = useCreatorColumnCatalog({ creatorTagsEnabled });
  const gmvFormatter = useMemo(
    () => new Intl.NumberFormat("en-US", { style: "currency", currency, notation: "compact", compactDisplay: "short" }),
    [currency],
  );

  const columns = useMemo<UnboxTableColumn<TransformedCreator>[]>(() => {
    const cols: UnboxTableColumn<TransformedCreator>[] = [
      {
        id: "creatorName",
        label: "Creator",
        sortable: true,
        minWidth: "260px",
        render: (_value, row) => {
          const { nickname, handle } = getCreatorName(row.nickname, row.creatorName);
          const displayName = nickname ?? handle;
          return (
            <div className="flex items-center gap-3">
              <Avatar
                size="md"
                src={row.thumbnail ?? undefined}
                initials={(nickname ?? row.creatorName)?.slice(0, 2).toUpperCase()}
                alt={displayName ?? "Creator"}
              />
              <div className="flex flex-col">
                <div className="text-sm font-medium text-primary">{displayName}</div>
                {nickname && handle && <div className="text-xs text-tertiary">{handle}</div>}
              </div>
            </div>
          );
        },
      },
    ];

    if (creatorTagsEnabled) {
      cols.push({
        id: "tags",
        label: "Tags",
        minWidth: "180px",
        render: (_value, row) =>
          row.isBlocked ? (
            <CreatorBlockedBadge block={row.block ?? { appliedBy: null }} />
          ) : (
            <CreatorTagsCellContent
              tiktokCreatorOpenId={row.tagOpenId}
              tagIds={row.tagIds}
              maxVisible={5}
              onManageAllTags={onManageAllTags}
            />
          ),
      });
    }

    cols.push(
      {
        id: "cohort",
        label: "Cohort",
        minWidth: "160px",
        cellClassName: "pl-0",
        render: (_value, row) => {
          if (row.isBlocked) {
            if (creatorTagsEnabled) return null;
            return <CreatorBlockedBadge block={row.block ?? { appliedBy: null }} />;
          }
          return (
            <CreatorCohortCellContent
              creatorId={row.creatorId}
              cohortId={row.cohortId}
              appliedBy={row.cohortAppliedBy}
              onManageAllCohorts={onManageAllCohorts}
            />
          );
        },
      },
      {
        id: "network",
        label: "Network",
        tooltip: NETWORK_COLUMN_TOOLTIP,
        minWidth: "130px",
        render: (_value, row) => (row.isBlocked ? null : <NetworkCellContent network={row.network} />),
      },
      {
        id: "matchQualityScore",
        label: "Match Quality",
        sortable: true,
        minWidth: "180px",
        render: (_value, row) => (row.isBlocked ? null : <MatchQualityCellContent score={row.matchQualityScore} />),
      },
      {
        id: "gmv",
        label: periodLabel ? `GMV (${periodLabel})` : "GMV (All Time)",
        sortable: true,
        minWidth: "140px",
        render: (_value, row) =>
          row.isBlocked ? null : (
            <TrendNumberCellContent value={row.gmv} trend={row.trendGmv} format={gmvFormatter.format} />
          ),
      },
      {
        id: "topGender",
        label: "Gender Demographic",
        minWidth: "120px",
        render: (_value, row) =>
          row.isBlocked ? null : <GenderDemoCellContent genderDistribution={row.genderDistribution} />,
      },
      {
        id: "topAgeRange",
        label: "Main Age Demo",
        minWidth: "120px",
        render: (_value, row) => (row.isBlocked ? null : <MainAgeDemoCellContent topAgeRange={row.topAgeRange} />),
      },
      {
        id: "videos",
        label: periodLabel ? `Videos (${periodLabel})` : "Videos (All Time)",
        sortable: true,
        minWidth: "100px",
        render: (_value, row) =>
          row.isBlocked ? null : (
            <div className="flex flex-row gap-1 items-center flex-wrap">
              {videoFormatter.format(row.videos)}
              <TrendBadge change={row.videosChange} dateRange={dateRange} />
            </div>
          ),
      },
      {
        id: "followerCount",
        label: "Followers",
        sortable: true,
        minWidth: "120px",
        render: (_value, row) =>
          row.isBlocked ? null : row.followerCount != null ? videoFormatter.format(row.followerCount) : "-",
      },
      ...metricColumns,
      {
        id: "list-actions",
        label: "",
        minWidth: "60px",
        render: (_value, row) => (
          <div className="flex flex-row gap-1 items-center justify-end">
            {!row.isBlocked && (
              <ButtonUtility
                size="xs"
                color="tertiary"
                tooltip="Invite to collaboration"
                icon={Announcement03}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  rowActions.onInvite(row.creatorId);
                }}
              />
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center justify-center w-8 h-8 hover:bg-secondary rounded transition-colors"
                  onClick={(e) => e.stopPropagation()}
                  aria-label="Creator actions"
                >
                  <DotsVertical className="w-5 h-5 text-tertiary" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                {row.isBlocked ? (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      if (row.tiktokCreatorOpenId) {
                        const { nickname, handle } = getCreatorName(row.nickname, row.creatorName);
                        rowActions.onUnblock(row.tiktokCreatorOpenId, nickname ?? handle ?? "Creator");
                      }
                    }}
                  >
                    <SlashCircle01 className="mr-2 size-4" />
                    Unblock Creator
                  </DropdownMenuItem>
                ) : (
                  <GroupedActionMenu
                    groups={buildCreatorActionGroups({
                      onInvite: () => rowActions.onInvite(row.creatorId),
                      onMessage: () => rowActions.onMessage(row.creatorId),
                      onAddToList: () => rowActions.onAddToList(row.creatorId),
                      onAddToCohort: () => rowActions.onAddToCohort(row.creatorId),
                      onBlock: () => {
                        if (row.tiktokCreatorOpenId) rowActions.onBlock(row.creatorId, row.tiktokCreatorOpenId);
                      },
                      onRemove: () => {},
                      cohortsEnabled: true,
                      blocklistEnabled: !!row.tiktokCreatorOpenId,
                    })
                      .map((group) => ({
                        ...group,
                        items: group.items.filter((i) => i.key !== "remove"),
                      }))
                      .filter((group) => group.items.length > 0)}
                  />
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    );

    return cols;
  }, [
    creatorTagsEnabled,
    periodLabel,
    gmvFormatter,
    dateRange,
    onManageAllTags,
    onManageAllCohorts,
    rowActions,
    metricColumns,
  ]);

  return (
    <>
      <UnboxTable<TransformedCreator>
        aria-label="Your Creators"
        columns={columns}
        rows={rows}
        rowId="creatorId"
        onClickRow={onClickRow}
        selection={selection}
        sorting={sorting}
        columnCatalog={columnCatalog}
        onHiddenSortColumn={onHiddenSortColumn}
        pagination={pagination}
        hasActiveFilters={hasActiveFilters}
        emptyState={{
          icon: UserCheck01,
          title: "All caught up on matches",
          description: "Try a different group, or browse all creators and filter by what matters to you.",
        }}
        filteredEmptyState={{
          icon: Users01,
          title: "No matches found right now",
          description:
            "Try browsing all creators, or explore groupings like Rising Stars to find creators right for your brand",
          onClearFilters,
        }}
        className="max-h-[60vh]"
      />

      {/* Row-level add to lists */}
      <AddtoListsModal
        selectedCreators={addToListsCreatorId ? [addToListsCreatorId] : []}
        onAddToList={() => setAddToListsCreatorId(undefined)}
        open={addToListsCreatorId !== undefined}
        onOpenChange={(open) => {
          if (!open) setAddToListsCreatorId(undefined);
        }}
      />

      {/* Row-level add to cohort */}
      <BulkCohortModal
        creatorIds={rowCohortCreatorId ? [rowCohortCreatorId] : []}
        open={rowCohortCreatorId !== undefined}
        onOpenChange={(open) => {
          if (!open) setRowCohortCreatorId(undefined);
        }}
        onApplied={() => setRowCohortCreatorId(undefined)}
      />

      {/* Row-level invite to collaboration. Same filter as the bulk modal — drop creators
            without a TikTok open id since the invite API requires one. */}
      {rowInviteCreatorId && (
        <InviteToCollaborationModal
          creators={
            rows
              ?.filter((c) => c.creatorId === rowInviteCreatorId && c.tiktokCreatorOpenId)
              .map((c) => ({
                openId: c.tiktokCreatorOpenId as string,
                username: c.creatorName ?? "",
                nickname: c.nickname ?? c.creatorName,
                avatar: c.thumbnail,
              })) || []
          }
          open={!!rowInviteCreatorId}
          onOpenChange={(open) => !open && setRowInviteCreatorId(undefined)}
        />
      )}

      {/* Row-level message creator */}
      {rowMessageCreatorId && (
        <MessageCreatorsDialog
          recipients={
            rows
              ?.filter((c) => c.creatorId === rowMessageCreatorId)
              .map((c) => ({
                id: c.creatorId,
                creatorId: c.creatorId,
                handle: c.creatorName ?? null,
                thumbnail: c.thumbnail,
              })) || []
          }
          open={!!rowMessageCreatorId}
          onOpenChange={(open) => !open && setRowMessageCreatorId(undefined)}
          onSuccess={() => setRowMessageCreatorId(undefined)}
        />
      )}

      {unblockTarget && (
        <UnblockCreatorModal
          creatorOpenId={unblockTarget.creatorOpenId}
          creatorName={unblockTarget.creatorName}
          open={!!unblockTarget}
          onOpenChange={(open) => {
            if (!open) setUnblockTarget(undefined);
          }}
        />
      )}
    </>
  );
}
